import sessionHelper from '~/utils/sessionHelper';

export interface CookiebotConsentData {
  necessary_consent: boolean;
  preferences_consent: boolean;
  statistics_consent: boolean;
  marketing_consent: boolean;
  source: 'cookiebot' | 'settings_page' | 'registration';
}

export interface CookiebotConsentResponse {
  message: string;
  consent: {
    id: number;
    userId: string;
    necessaryConsent: boolean;
    preferencesConsent: boolean;
    statisticsConsent: boolean;
    marketingConsent: boolean;
    source: string;
    createdAt: string;
    updatedAt: string;
  };
}

declare global {
  interface Window {
    Cookiebot?: {
      consent: {
        necessary: boolean;
        preferences: boolean;
        statistics: boolean;
        marketing: boolean;
      };
      renew: () => void;
      submitCustomConsent: (optinPreferences: boolean, optinStatistics: boolean, optinMarketing: boolean) => void;
    };
  }
}

const RETRY_BASE_DELAY_MS = 500;
const RETRY_MAX_DELAY_MS = 5000;

const getRetryDelay = (attempt: number): number => {
  const delay = RETRY_BASE_DELAY_MS * 2 ** attempt;
  return Math.min(delay, RETRY_MAX_DELAY_MS);
};

type ConsentErrorResult =
  | { shouldStop: true }
  | { shouldStop: false; delay: number };

export default function useCookiebotConsent () {
  const isCookiebotAvailable = (): boolean => {
    return typeof window !== 'undefined' && !!window.Cookiebot;
  };

  const getConsentData = (source: CookiebotConsentData['source'] = 'cookiebot'): CookiebotConsentData | null => {
    if (!isCookiebotAvailable() || !window.Cookiebot) {
      return null;
    }

    return {
      necessary_consent: window.Cookiebot.consent.necessary,
      preferences_consent: window.Cookiebot.consent.preferences,
      statistics_consent: window.Cookiebot.consent.statistics,
      marketing_consent: window.Cookiebot.consent.marketing,
      source,
    };
  };

  const sendConsentToBackend = async (
    consentData: CookiebotConsentData,
  ): Promise<CookiebotConsentResponse> => {
    return await $fetch<CookiebotConsentResponse>('/api/auth/user/consent', {
      method: 'POST',
      body: consentData,
      onRequest: sessionHelper.fetchOnRequestInterceptor,
    });
  };

  const handleConsentError = (
    error: any,
    attempt: number,
    maxRetries: number,
  ): ConsentErrorResult => {
    const isLastAttempt = attempt === maxRetries - 1;
    const statusCode = error?.statusCode;

    if (statusCode && [401, 419].includes(statusCode)) {
      console.error('[Cookiebot] Authentication error while sending consent. User may not be logged in.', {
        statusCode,
        error,
      });
      return { shouldStop: true };
    }

    if (statusCode === 422) {
      console.error('[Cookiebot] Validation error while sending consent. Invalid consent data.', {
        statusCode,
        error,
      });
      return { shouldStop: true };
    }

    if (isLastAttempt) {
      console.error('[Cookiebot] Failed to send consent after maximum retries', {
        attempts: maxRetries,
        statusCode,
        error,
      });

      const { dispatchMessage } = useSystemMessages();
      dispatchMessage(
        'Consent Tracking Failed',
        'warn',
        'Your cookie preferences could not be saved. Please try again later.',
        5000,
      );

      return { shouldStop: true };
    }

    const delay = getRetryDelay(attempt);
    return { shouldStop: false, delay };
  };

  const sendConsentWithRetry = async (
    maxRetries: number = 3,
    source: CookiebotConsentData['source'] = 'cookiebot',
  ): Promise<boolean> => {
    const consentData = getConsentData(source) as CookiebotConsentData;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        await sendConsentToBackend(consentData);
        return true;
      } catch (error: any) {
        const result = handleConsentError(error, attempt, maxRetries);

        if (result.shouldStop) {
          return false;
        }

        await new Promise(resolve => setTimeout(resolve, result.delay));
      }
    }

    return false;
  };

  const reopenConsentDialog = (): void => {
    if (!isCookiebotAvailable() || !window.Cookiebot) {
      return;
    }

    window.Cookiebot.renew();
  };

  const enableStatisticsConsent = (): boolean => {
    if (!isCookiebotAvailable() || typeof window.Cookiebot?.submitCustomConsent !== 'function') {
      return false;
    }

    const { preferences, marketing } = window.Cookiebot.consent;

    try {
      window.Cookiebot.submitCustomConsent(preferences, true, marketing);
      return true;
    } catch (error) {
      console.error('[Cookiebot] Failed to submit the statistics consent opt-in', { error });
      return false;
    }
  };

  return {
    isCookiebotAvailable,
    getConsentData,
    sendConsentToBackend,
    sendConsentWithRetry,
    reopenConsentDialog,
    enableStatisticsConsent,
  };
}
